import { useCallback, useState, useEffect } from "react";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import { DragDrop, useUppy } from "@uppy/react";
import useGenerateUploadDailyFormWorkOrderFileUrl from "hooks/fleet/useGenerateUploadDailyFormWorkOrderFileUrl";
import useModalState from "hooks/useModalState";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Modal, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import isImage from "utils/images/isImage";

import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";

interface NewFile {
  name: string;
  path: string;
}

interface ExistingFile {
  id: string;
  name: string;
  path: string;
}

interface FileChangeData {
  newFiles: NewFile[];
  removedFileIds: string[];
}

interface AddFilesToWorkOrderProps {
  onFilesChange: (data: FileChangeData) => void;
  existingFiles?: ExistingFile[];
}

export default function AddFilesToWorkOrder({
  onFilesChange,
  existingFiles = [],
}: AddFilesToWorkOrderProps) {
  const [generateUploadWorkOrderFileUrl, { getData, isSuccess }] =
    useGenerateUploadDailyFormWorkOrderFileUrl();

  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [previews, setPreviews] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const { open: modalOpen, onOpen: openModal, onClose: closeModal } = useModalState();
  const [selectedImage, setSelectedImage] = useState<{ src: string; name: string } | null>(null);

  useEffect(() => {
    const existingPreviews: { [key: string]: string } = {};
    existingFiles.forEach((file) => {
      if (isImage(file.name)) {
        existingPreviews[file.name] = `${import.meta.env.VITE_CDN_BASE_URL}/${file.path}`;
      }
    });
    setPreviews(existingPreviews);
  }, [existingFiles]);

  useEffect(() => {
    onFilesChange({
      newFiles,
      removedFileIds,
    });
  }, [newFiles, removedFileIds, onFilesChange]);

  const onSuccess = useCallback((file: { name: string; path: string }) => {
    setNewFiles((prevFiles) => [...prevFiles, file]);
  }, []);

  const readImage = useCallback((file: any, fileName: string) => {
    const blob = new Blob([file.data], { type: file.type });
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      setPreviews((prev) => ({ ...prev, [fileName]: reader.result as string }));
    };
  }, []);

  const handleViewImage = useCallback(
    (imageSrc: string, fileName: string) => {
      setSelectedImage({ src: imageSrc, name: fileName });
      openModal();
    },
    [openModal]
  );

  const handleCloseModal = useCallback(() => {
    closeModal();
    setSelectedImage(null);
  }, [closeModal]);

  const uppy = useUppy(() => {
    const _uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: ["image/*", ".jpg", ".jpeg", ".png", ".pdf"],
      },
    }).use(AwsS3, {
      async getUploadParameters(file) {
        const result = await generateUploadWorkOrderFileUrl({
          variables: {
            contentType: file.type,
            filename: file.name,
          },
        });
        const success = isSuccess(result.data);
        if (success) {
          const data = getData(result);
          return {
            method: data.method as "PUT",
            url: data.url,
            fields: {},
            headers: {
              "Content-Type": data.contentType,
              ...(data.contentDisposition && { "Content-Disposition": data.contentDisposition }),
            },
          };
        } else {
          throw new Error("Could not upload the file");
        }
      },
    });

    _uppy.on("upload", () => {
      setStatus("pending");
      setErrorMessage("");
    });

    _uppy.on("upload-success", (file, response) => {
      if (response.status === 200) {
        const path = "tmp/" + response.uploadURL.split("/tmp/")[1];
        onSuccess({ name: file.name, path });
        setStatus("fulfilled");
        if (file.type.startsWith("image/")) {
          readImage(file, file.name);
        }
        _uppy.removeFile(file.id);
      }
    });

    _uppy.on("restriction-failed", (file, error) => {
      if (error) {
        setStatus("rejected");
        setErrorMessage(error.message);
      }
    });

    _uppy.on("upload-error", (file) => {
      if (file) {
        setErrorMessage("An error has occurred while uploading this file");
        setStatus("rejected");
        _uppy.removeFile(file.id);
      }
    });

    return _uppy;
  });

  const removeFile = useCallback(
    (index: number, isExisting: boolean = false) => {
      if (isExisting) {
        const fileToRemove = existingFiles[index];
        if (fileToRemove) {
          setRemovedFileIds((prev) => [...prev, fileToRemove.id]);
          setPreviews((prev) => {
            const newPreviews = { ...prev };
            delete newPreviews[fileToRemove.name];
            return newPreviews;
          });
        }
      } else {
        const fileToRemove = newFiles[index];
        const updatedNewFiles = newFiles.filter((_, i) => i !== index);
        setNewFiles(updatedNewFiles);

        if (fileToRemove) {
          setPreviews((prev) => {
            const newPreviews = { ...prev };
            delete newPreviews[fileToRemove.name];
            return newPreviews;
          });
        }
      }
    },
    [newFiles, existingFiles]
  );

  const visibleExistingFiles = existingFiles.filter((file) => !removedFileIds.includes(file.id));
  const totalFiles = visibleExistingFiles.length + newFiles.length;

  return (
    <MDBox>
      <MDTypography variant="h6" mb={2}>
        Attachments
      </MDTypography>

      <MDBox mb={2}>
        <DragDrop
          width="100%"
          height="150px"
          uppy={uppy}
          locale={{
            strings: {
              dropHereOr: "Drop files here or %{browse}",
              browse: "browse",
            },
          }}
        />
      </MDBox>

      {status === "pending" && (
        <MDTypography variant="body2" color="info" mb={1}>
          Uploading...
        </MDTypography>
      )}

      {errorMessage && (
        <MDTypography variant="body2" color="error" mb={1}>
          {errorMessage}
        </MDTypography>
      )}

      {totalFiles > 0 && (
        <MDBox>
          <MDTypography variant="body2" mb={1}>
            Files:
          </MDTypography>
          <Grid container spacing={1}>
            {visibleExistingFiles.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={`existing-${file.id}`}>
                <MDBox
                  sx={{
                    pt: 2,
                    pb: 2,
                    pl: 2,
                    pr: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    minHeight: "64px",
                  }}
                >
                  {previews[file.name] && (
                    <MDBox
                      component="img"
                      src={previews[file.name]}
                      alt={file.name}
                      width="40px"
                      height="40px"
                      borderRadius="4px"
                      sx={{ objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <MDBox flexGrow={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {file.name}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap="4px">
                    {previews[file.name] && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => handleViewImage(previews[file.name], file.name)}
                        startIcon={<VisibilityIcon />}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      ></MDButton>
                    )}
                    <MDButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFile(index, true)}
                      startIcon={<DeleteIcon />}
                      sx={{ minWidth: "auto", p: 0.5 }}
                    ></MDButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}

            {newFiles.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={`new-${index}`}>
                <MDBox
                  sx={{
                    pt: 2,
                    pb: 2,
                    pl: 2,
                    pr: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    minHeight: "64px",
                  }}
                >
                  {previews[file.name] && (
                    <MDBox
                      component="img"
                      src={previews[file.name]}
                      alt={file.name}
                      width="40px"
                      height="40px"
                      borderRadius="4px"
                      sx={{ objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <MDBox flexGrow={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {file.name}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap="4px">
                    {previews[file.name] && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => handleViewImage(previews[file.name], file.name)}
                        startIcon={<VisibilityIcon />}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      ></MDButton>
                    )}
                    <MDButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFile(index, false)}
                      startIcon={<DeleteIcon />}
                      sx={{ minWidth: "auto", p: 0.5 }}
                    ></MDButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDBox
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <MDBox
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
          {selectedImage && (
            <>
              <MDBox p={2} borderBottom="1px solid #e0e0e0">
                <MDTypography variant="h6">{selectedImage.name}</MDTypography>
              </MDBox>
              <MDBox
                component="img"
                src={selectedImage.src}
                alt={selectedImage.name}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "calc(90vh - 80px)",
                  objectFit: "contain",
                }}
              />
            </>
          )}
        </MDBox>
      </Modal>
    </MDBox>
  );
}
